/**
 * Closed flashcard face renderer. Six patterns, and nothing else:
 * image `![alt](url)`, strong `**text**`, highlight `==text==`,
 * display formula `$$text$$`, inline formula `$text$`, emphasis `*text*`.
 * A line that is `- item` groups into a list. Unrecognized input, including
 * HTML and unclosed markers, stays literal text.
 *
 * Images render through `ImagemDoCartao`, which resolves a `/files/<chave>`
 * target through the authenticated fetch (or `resolveImageUrl`, for stories).
 */
import { ReactNode } from 'react';

import ImagemDoCartao from './ImagemDoCartao';

export interface CardMarkdownProps {
  /** Face text, the same string stored in `front` / `back`. */
  value: string;
  className?: string;
  /** Story/preview override for a `/files/<chave>` image target — skips the authenticated fetch. */
  resolveImageUrl?: ResolveImageUrl;
}

const LIST_ITEM = /^- (.*)$/;
const IMAGE = /^!\[([^\n\]]*)\]\(([^\s\n)]+)\)/;

const displayFormulaClass =
  'my-1 block w-full text-center font-mono text-[0.92em] text-ink';
const inlineFormulaClass =
  'rounded-sm bg-surface-muted px-1 font-mono text-[0.92em] text-ink';

type KeyGen = () => number;
type ResolveImageUrl = (fileKey: string) => string;

type Scan =
  | { kind: 'node'; next: number; node: ReactNode }
  | { kind: 'literal'; next: number; literal: string };

function makeKeys(): KeyGen {
  let n = 0;
  return () => n++;
}

/** Same-origin path (`/x`, not `//`) or an http(s) URL. Anything else is unsafe. */
function isSafeImageSrc(url: string): boolean {
  if (url.startsWith('//')) return false;
  if (url.startsWith('/')) return true;
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

function lineEnd(source: string, index: number): number {
  const newline = source.indexOf('\n', index);
  return newline === -1 ? source.length : newline;
}

function parseInline(
  source: string,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let literal = '';
  let index = 0;

  const flush = () => {
    if (literal.length === 0) return;
    nodes.push(literal);
    literal = '';
  };

  while (index < source.length) {
    const step = scanAt(source, index, key, resolveImageUrl);
    if (step.next <= index) {
      literal += source[index];
      index += 1;
      continue;
    }
    if (step.kind === 'node') {
      flush();
      nodes.push(step.node);
    } else {
      literal += step.literal;
    }
    index = step.next;
  }

  flush();
  return nodes;
}

function scanAt(
  source: string,
  index: number,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): Scan {
  if (source.startsWith('![', index)) {
    const image = matchImage(source, index, key, resolveImageUrl);
    if (image) return image;
  }
  if (source.startsWith('**', index)) return matchStrong(source, index, key, resolveImageUrl);
  if (source.startsWith('==', index)) return matchHighlight(source, index, key, resolveImageUrl);
  if (source.startsWith('$$', index)) return matchDisplayFormula(source, index, key);
  if (source[index] === '$') return matchInlineFormula(source, index, key);
  if (source[index] === '*') return matchEmphasis(source, index, key, resolveImageUrl);
  return { kind: 'literal', literal: source[index], next: index + 1 };
}

function matchImage(
  source: string,
  index: number,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): Scan | null {
  const match = IMAGE.exec(source.slice(index));
  if (!match) return null;
  const token = match[0];
  if (index + token.length > lineEnd(source, index)) return null;
  const alt = match[1];
  const url = match[2];
  if (!isSafeImageSrc(url)) {
    return { kind: 'literal', literal: token, next: index + token.length };
  }
  return {
    kind: 'node',
    next: index + token.length,
    node: (
      <ImagemDoCartao key={key()} alt={alt} src={url} resolveImageUrl={resolveImageUrl} />
    ),
  };
}

function matchPaired(
  source: string,
  index: number,
  delimiter: string,
  wrap: (children: ReactNode, nodeKey: number) => ReactNode,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): Scan {
  const openEnd = index + delimiter.length;
  const limit = lineEnd(source, index);
  const closer = source.indexOf(delimiter, openEnd);
  if (closer < 0 || closer + delimiter.length > limit) {
    return { kind: 'literal', literal: delimiter, next: openEnd };
  }
  const nodeKey = key();
  const children = parseInline(source.slice(openEnd, closer), key, resolveImageUrl);
  return {
    kind: 'node',
    node: wrap(children, nodeKey),
    next: closer + delimiter.length,
  };
}

function matchStrong(
  source: string,
  index: number,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): Scan {
  return matchPaired(
    source,
    index,
    '**',
    (children, nodeKey) => (
      <strong key={nodeKey} className="font-bold">
        {children}
      </strong>
    ),
    key,
    resolveImageUrl,
  );
}

function matchHighlight(
  source: string,
  index: number,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): Scan {
  return matchPaired(
    source,
    index,
    '==',
    (children, nodeKey) => (
      <mark key={nodeKey} className="rounded-sm bg-mark/40 px-0.5 text-ink">
        {children}
      </mark>
    ),
    key,
    resolveImageUrl,
  );
}

function matchLiteralSpan(
  source: string,
  index: number,
  delimiter: string,
  className: string,
  nonEmpty: boolean,
  key: KeyGen,
): Scan {
  const openEnd = index + delimiter.length;
  const limit = lineEnd(source, index);
  const closer = source.indexOf(delimiter, openEnd);
  if (closer < 0 || closer + delimiter.length > limit) {
    return { kind: 'literal', literal: delimiter, next: openEnd };
  }
  const inner = source.slice(openEnd, closer);
  if (nonEmpty && inner.length === 0) {
    return { kind: 'literal', literal: delimiter, next: openEnd };
  }
  return {
    kind: 'node',
    next: closer + delimiter.length,
    node: (
      <span key={key()} className={className}>
        {inner}
      </span>
    ),
  };
}

function matchDisplayFormula(source: string, index: number, key: KeyGen): Scan {
  return matchLiteralSpan(source, index, '$$', displayFormulaClass, false, key);
}

function matchInlineFormula(source: string, index: number, key: KeyGen): Scan {
  return matchLiteralSpan(source, index, '$', inlineFormulaClass, true, key);
}

function matchEmphasis(
  source: string,
  index: number,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): Scan {
  const limit = lineEnd(source, index);
  let closer = -1;
  for (let j = index + 1; j < limit; j++) {
    if (source[j] !== '*') continue;
    // A `*` that belongs to `**` is strong, not an emphasis closer.
    if (source.startsWith('**', j)) {
      j += 1;
      continue;
    }
    closer = j;
    break;
  }
  if (closer < 0) return { kind: 'literal', literal: '*', next: index + 1 };
  const nodeKey = key();
  const children = parseInline(source.slice(index + 1, closer), key, resolveImageUrl);
  return {
    kind: 'node',
    next: closer + 1,
    node: (
      <em key={nodeKey} className="italic">
        {children}
      </em>
    ),
  };
}

function renderBlocks(
  value: string,
  key: KeyGen,
  resolveImageUrl: ResolveImageUrl | undefined,
): ReactNode[] {
  const lines = value.split('\n');
  const nodes: ReactNode[] = [];
  let text: string[] = [];
  let items: string[] = [];

  const flushText = () => {
    if (text.length === 0) return;
    nodes.push(...parseInline(text.join('\n'), key, resolveImageUrl));
    text = [];
  };

  const flushList = () => {
    if (items.length === 0) return;
    const bodies = items;
    items = [];
    nodes.push(
      <ul key={key()} className="my-1 list-disc pl-6 text-left whitespace-normal">
        {bodies.map((body) => (
          <li key={key()}>{parseInline(body, key, resolveImageUrl)}</li>
        ))}
      </ul>,
    );
  };

  for (const line of lines) {
    const item = LIST_ITEM.exec(line);
    if (item) {
      flushText();
      items.push(item[1]);
    } else {
      flushList();
      text.push(line);
    }
  }

  flushText();
  flushList();
  return nodes;
}

export default function CardMarkdown({
  value,
  className,
  resolveImageUrl,
}: CardMarkdownProps): JSX.Element {
  const rootClass = className
    ? `min-w-0 max-w-full whitespace-pre-wrap break-words ${className}`
    : 'min-w-0 max-w-full whitespace-pre-wrap break-words';
  return <div className={rootClass}>{renderBlocks(value, makeKeys(), resolveImageUrl)}</div>;
}
